import clsx from "clsx";
import {
  addMonths,
  eachDayOfInterval,
  format,
  startOfMonth,
  subMonths,
} from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import { Button, LoadPanel } from "devextreme-react";
import { Calendar, CalendarRef } from "devextreme-react/calendar";
import _ from "lodash";
import { useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/auth";
import {
  EmploymentIndex,
  employmentKey,
  isDayWithinEmployment,
} from "../../../lib/employment";
import { ThemeContext } from "../../../theme/theme";
import { useScreenSize } from "../../../utils/media-query";
import type { AbsenceData2, ScheduleDay2 } from "../types";
import { getCellClassAndIcon } from "../utils/absenceClassHelpers";
import { CalendarCell2 } from "./CalendarCell";

interface EmployeeCalendarView2Props {
  currentMonth: Date;
  setCurrentMonth: (date: Date) => void;
  schedule: Record<string, ScheduleDay2>;
  holidayData: Record<number, AbsenceData2[]>;
  holidayLimitUser: { days: number; hours: number };
  isLoading: boolean;
  onDoubleClick: (date: Date, employeeId: number, companyId: number) => void;
  lastCellClickTime: React.MutableRefObject<number>;
  employmentIndex: EmploymentIndex;
}

export function EmployeeCalendarView2({
  currentMonth,
  setCurrentMonth,
  schedule,
  holidayData,
  holidayLimitUser,
  isLoading,
  onDoubleClick,
  lastCellClickTime,
  employmentIndex,
}: EmployeeCalendarView2Props) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const themeContext = useContext(ThemeContext);
  const calendarRef = useRef<CalendarRef>(null);
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  // Przedziały zatrudnienia zalogowanego pracownika - do blokady dni poza umową we własnym kalendarzu.
  const ownEmploymentSpans = employmentIndex.get(
    employmentKey(user?.companyId ?? 0, user?.employeeId ?? 0),
  );

  const startDate = useMemo(
    () => new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1),
    [currentMonth],
  );
  const endDate = useMemo(
    () => new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0),
    [currentMonth],
  );

  const limitInfo = `${t("Amount of remaining annual leave:")} ${t(
    "holiday.daysHours",
    holidayLimitUser,
  )}`;

  if (isMobile) {
    const employeeId = user?.employeeId || 0;
    const companyId = user?.companyId || 0;
    const todayStr = new Date().toLocaleDateString("en-CA");

    return (
      <div style={{ position: "relative" }}>
        <LoadPanel
          visible={isLoading}
          shading
          shadingColor="rgba(0, 0, 0, 0.3)"
        />
        <div
          className={clsx(
            "sticky top-0 z-10 flex items-center gap-1 py-2",
            themeContext?.theme === "dark" ? "bg-[#1e1e1e]" : "bg-white",
          )}
        >
          <Button
            icon="chevronprev"
            stylingMode="text"
            onClick={() =>
              setCurrentMonth(startOfMonth(subMonths(currentMonth, 1)))
            }
          />
          <div className="flex-1 text-center text-base font-semibold">
            {`${_.upperFirst(
              format(currentMonth, "LLLL", {
                locale: i18n.language === "en" ? enUS : pl,
              }),
            )} ${format(currentMonth, "yyyy")}`}
          </div>
          <Button
            icon="chevronnext"
            stylingMode="text"
            onClick={() =>
              setCurrentMonth(startOfMonth(addMonths(currentMonth, 1)))
            }
          />
          <Button
            text={i18n.language === "en" ? "Today" : "Dziś"}
            stylingMode="outlined"
            onClick={() => setCurrentMonth(startOfMonth(new Date()))}
          />
        </div>
        <div className="text-sm mb-3 px-1">{limitInfo}</div>
        <div className="flex flex-col gap-2 pb-4">
          {eachDayOfInterval({ start: startDate, end: endDate }).map((day) => {
            const formattedDate = day.toLocaleDateString("en-CA");
            const scheduleData = schedule[formattedDate];
            const dayType = scheduleData?.dayType ?? null;
            const { changeClass, icon, absenceDataForCell } =
              getCellClassAndIcon(
                formattedDate,
                themeContext?.theme,
                employeeId,
                holidayData,
                dayType,
              );
            const dayShort = _.upperFirst(
              format(day, "EEEEEE", {
                locale: i18n.language === "en" ? enUS : pl,
              }),
            );
            const isToday = formattedDate === todayStr;
            const outsideContract = !isDayWithinEmployment(
              ownEmploymentSpans,
              day,
            );
            const onTap = () => onDoubleClick(day, employeeId, companyId);

            return (
              <div
                key={formattedDate}
                className={clsx(
                  "flex items-stretch gap-3 p-3 rounded-lg shadow-sm border-2",
                  changeClass,
                  isToday ? "border-[#3c8dbc]" : "border-transparent",
                  outsideContract
                    ? "opacity-40 cursor-not-allowed"
                    : "cursor-pointer",
                )}
                title={
                  outsideContract
                    ? t("holidayPopup.outsideEmployment")
                    : undefined
                }
                onClick={outsideContract ? undefined : onTap}
              >
                <div className="flex flex-col items-center justify-center min-w-[48px] font-bold">
                  <div className="text-2xl leading-none">{day.getDate()}</div>
                  <div className="text-[11px] uppercase opacity-80 mt-1">
                    {dayShort}
                  </div>
                </div>
                <div className="flex-1 flex flex-col justify-center text-sm min-w-0">
                  {absenceDataForCell ? (
                    <>
                      <div className="font-medium break-words">
                        {absenceDataForCell.name}
                      </div>
                      {absenceDataForCell.note ? (
                        <div className="text-xs opacity-85 break-words">
                          {absenceDataForCell.note}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
                {icon ? (
                  <div className="flex items-center text-xl">{icon}</div>
                ) : null}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          aria-label={t("holidayPopup.confirmHoliday")}
          onClick={() => onDoubleClick(new Date(), employeeId, companyId)}
          style={{
            position: "fixed",
            right: 18,
            bottom: 22,
            width: 56,
            height: 56,
            borderRadius: "50%",
            border: "none",
            background: "#3c8dbc",
            color: "#fff",
            fontSize: 32,
            lineHeight: 1,
            boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
            cursor: "pointer",
            zIndex: 1500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          +
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <LoadPanel
        visible={isLoading}
        shading
        shadingColor="rgba(0, 0, 0, 0.3)"
        position={{ of: ".calendar-data" }}
      />
      <div className="weekly">
        <Calendar
          ref={calendarRef}
          className="calendar-data mx-auto max-w-5xl"
          defaultValue={[]}
          firstDayOfWeek={1}
          width="100%"
          height={750}
          minZoomLevel="decade"
          cellRender={({ date, text, view }) => (
            <CalendarCell2
              date={date}
              text={text}
              view={view}
              theme={themeContext?.theme}
              employeeId={user?.employeeId || 0}
              holidayData={holidayData}
              schedule={schedule}
              startDate={startDate}
              endDate={endDate}
              onDoubleClick={(d) =>
                onDoubleClick(d, user?.employeeId || 0, user?.companyId || 0)
              }
              lastCellClickTime={lastCellClickTime}
            />
          )}
          disabledDates={({ date, view }) =>
            view === "month" &&
            (date.getMonth() !== startDate.getMonth() ||
              !isDayWithinEmployment(ownEmploymentSpans, date))
          }
          onOptionChanged={(event) => {
            if (event.fullName === "currentDate") {
              const currentValue = event.value as Date;
              const previousValue = event.previousValue as Date;
              if (currentValue.getMonth() !== previousValue.getMonth()) {
                setCurrentMonth(currentValue);
              }
            }
          }}
          selectionMode="multiple"
        />
      </div>
      <div>{limitInfo}</div>
    </div>
  );
}
